'use client'
import type { FC, MouseEvent, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import {
  Box,
  type BoxProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  VStack
} from '@chakra-ui/react'
import { usePathname } from 'next/navigation'
import GrandNextLink from '../../common/GrandNextLink'
import HoverableComponent from '../../common/HoverableComponent'
import HoverableTextUnderline from '../../common/HoverableTextUnderline'
import type { GrandDividerProps } from '../../common'

export interface MenuSubItem {
  id: string
  text: string
  path: string
  isExternal?: boolean
}

export interface MenuItem {
  id: string
  text: string
  /** An empty path renders an entry that only opens its `subItems` dropdown. */
  path: string
  isExternal?: boolean
  /** Turns the item into a hover dropdown of plain links. */
  subItems?: MenuSubItem[]
  isCurrent?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  other?: any[]
  onHoverEnter?: () => void
  onHoverLeave?: () => void
  textComponentProps?: BoxProps
  hoveredBorderColor?: GrandDividerProps['borderColor']
}

const externalLinkProps = { target: '_blank', rel: 'noopener noreferrer' }

const MenuItemComponent: FC<MenuItem> = ({
  onHoverEnter,
  onHoverLeave,
  text,
  path,
  isExternal,
  subItems,
  isCurrent,
  onClick,
  textComponentProps,
  hoveredBorderColor
}) => {
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    if (isHovered) {
      onHoverEnter?.()
    } else {
      onHoverLeave?.()
    }
  }, [isHovered, onHoverEnter, onHoverLeave])

  const label = (
    <HoverableComponent
      borderRadius="sm"
      color={isCurrent ? 'primary' : 'text.default'}
      cursor="pointer"
      flexGrow={0}
      fontWeight="medium"
      onHover={setIsHovered}
      px={2}
      py={3}
      w="auto"
      {...textComponentProps}
    >
      <HoverableTextUnderline
        fontSize="xs"
        text={text.toUpperCase()}
        hoveredBorderColor={hoveredBorderColor}
        {...{ isHovered }}
      />
    </HoverableComponent>
  )

  const link = path ? (
    <GrandNextLink
      aria-label={text}
      href={path}
      textDecoration="none!important"
      {...{ onClick }}
      {...(isExternal ? externalLinkProps : {})}
    >
      {label}
    </GrandNextLink>
  ) : (
    label
  )

  if (!subItems?.length) return link

  return <MenuItemDropdown trigger={link} subItems={subItems} />
}

// Same theme hook TopNavContentMenuItem uses for the active top item, so the dropdown picks up each
// store's nav colour (falls back to primary).
const navActiveColor =
  'var(--chakra-colors-component-TopNavContent-MenuItem-_active-color, var(--chakra-colors-primary))'
const navActiveTint = `color-mix(in srgb, ${navActiveColor} 8%, transparent)`

const MenuItemDropdown: FC<{ trigger: ReactNode; subItems: MenuSubItem[] }> = ({
  trigger,
  subItems
}) => {
  const pathname = usePathname()

  return (
    <Popover
      trigger="hover"
      placement="bottom-start"
      // attached to the nav; -8px lines the item text up under the label text
      offset={[-8, 4]}
      openDelay={80}
      closeDelay={150}
      isLazy
    >
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Box display="inline-flex">{trigger}</Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              // above the fixed top nav (zIndex 9000)
              rootProps={{ zIndex: 9001 }}
              bg="background.default"
              w="auto"
              minW="220px"
              p={1}
              border="1px solid"
              borderColor="blackAlpha.100"
              borderRadius="md"
              boxShadow="lg"
              _focusVisible={{ outline: 'none' }}
            >
              <VStack align="stretch" spacing={0.5}>
                {subItems.map((sub) => {
                  const isCurrent = pathname === sub.path

                  return (
                    <GrandNextLink
                      key={sub.id}
                      aria-label={sub.text}
                      aria-current={isCurrent ? 'page' : undefined}
                      href={sub.path}
                      onClick={onClose}
                      {...(sub.isExternal ? externalLinkProps : {})}
                      px={3}
                      py={2.5}
                      borderRadius="sm"
                      fontSize="sm"
                      fontWeight={isCurrent ? 'semibold' : 'medium'}
                      lineHeight="short"
                      color={isCurrent ? navActiveColor : 'text.default'}
                      bg={isCurrent ? navActiveTint : 'transparent'}
                      boxShadow={isCurrent ? `inset 2px 0 0 ${navActiveColor}` : 'none'}
                      textDecoration="none!important"
                      transition="background 0.15s ease, color 0.15s ease"
                      _hover={{ color: navActiveColor, bg: navActiveTint }}
                      _focusVisible={{ color: navActiveColor, bg: navActiveTint, outline: 'none' }}
                    >
                      {sub.text}
                    </GrandNextLink>
                  )
                })}
              </VStack>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  )
}

export default MenuItemComponent
